import React, { useState, useEffect } from 'react';
import { Star, Send, CheckCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';

const TestimonialForm = ({ userId }) => {
  const [content, setContent] = useState('');
  const [rating, setRating] = useState(5);
  const [companyName, setCompanyName] = useState('');
  const [allowNameDisplay, setAllowNameDisplay] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingTestimonial, setExistingTestimonial] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check if user already submitted a testimonial
    const checkExisting = async () => {
      try {
        const res = await fetch(`${API_URL}/api/testimonials/my?user_id=${userId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.testimonial) {
            setExistingTestimonial(data.testimonial);
          }
        }
      } catch (e) {
        // Ignore
      }
    };
    if (userId) checkExisting();
  }, [userId, API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() || content.length < 20) return;
    
    setIsSubmitting(true);
    
    try {
      const res = await fetch(`${API_URL}/api/testimonials/submit?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: content.trim(),
          rating,
          company_name: companyName || null,
          allow_name_display: allowNameDisplay
        })
      });
      
      if (res.ok) {
        setSubmitted(true);
        setExistingTestimonial({ content, rating, status: 'pending' });
      }
    } catch (e) {
      console.error('Submit error:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Already submitted
  if (existingTestimonial) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            {existingTestimonial.status === 'approved' ? (
              <>
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                Votre témoignage est en ligne
              </>
            ) : (
              <>
                <Clock className="h-5 w-5 text-amber-500" />
                Témoignage en attente de validation
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  className={`h-4 w-4 ${i < existingTestimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} 
                />
              ))}
            </div>
            <p className="text-slate-700 text-sm italic">"{existingTestimonial.content}"</p>
          </div>
          <p className="text-xs text-slate-500 mt-3">
            Merci pour votre confiance !
          </p>
        </CardContent>
      </Card>
    );
  }

  // Submitted just now
  if (submitted) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Merci pour votre témoignage !
          </h3>
          <p className="text-slate-600 text-sm">
            Il sera visible sur le site après validation par notre équipe.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Partagez votre expérience</CardTitle>
        <p className="text-sm text-slate-500">
          Votre avis aide d'autres entrepreneurs à découvrir SmartFacture AI
        </p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating */}
          <div>
            <Label>Votre note</Label>
            <div className="flex gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="focus:outline-none"
                >
                  <Star 
                    className={`h-8 w-8 transition-colors ${
                      star <= rating 
                        ? 'text-yellow-400 fill-yellow-400' 
                        : 'text-slate-300 hover:text-yellow-200'
                    }`} 
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div>
            <Label htmlFor="testimonial-content">Votre témoignage</Label>
            <Textarea
              id="testimonial-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Comment SmartFacture AI vous aide au quotidien ?"
              className="mt-1 min-h-[100px]"
              maxLength={500}
            />
            <p className="text-xs text-slate-400 mt-1">{content.length}/500 caractères (min. 20)</p>
          </div>

          {/* Company name (optional) */}
          <div>
            <Label htmlFor="company-name">Nom de votre entreprise (optionnel)</Label>
            <Input
              id="company-name"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Ex: Studio Design"
              className="mt-1"
            />
          </div>

          {/* Allow name display */}
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={allowNameDisplay}
              onChange={(e) => setAllowNameDisplay(e.target.checked)}
              className="rounded text-blue-600"
            />
            <span className="text-sm text-slate-600">
              J'accepte que mon prénom et initiale soient affichés
            </span>
          </label>

          <Button 
            type="submit" 
            disabled={isSubmitting || content.length < 20}
            className="w-full"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                Envoi...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Envoyer mon témoignage
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default TestimonialForm;
